import { useAuth0, User } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { fuelLocationsOptions, useUpdateFuelLocation } from "./api";
import type { FuelStation } from "./api";
import ColumnFilter from "components/column-filter";
import { useMemo } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

export default function FuelLocationsPage() {
  const auth = useAuth0();
  const user = auth.user as Exclude<User, undefined>;
  const companyId = user["https://ifuelsmart.com/company"] as string;

  const { data: fuelLocations = [] } = useQuery(fuelLocationsOptions(companyId));
  const toggleFuelLocation = useUpdateFuelLocation();

  const data = useMemo(() => [...fuelLocations], [fuelLocations]);

  const columns = useMemo<ColumnDef<FuelStation>[]>(
    () => [
      { header: "Id", accessorKey: "id" },
      { header: "Store Number", accessorKey: "storeNumber" },
      { header: "Discounted Price", accessorKey: "discountedPrice" },
      { header: "City", accessorKey: "city", meta: { filterComponent: ColumnFilter } },
      { header: "State", accessorKey: "state", meta: { filterComponent: ColumnFilter } },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ getValue }) =>
          getValue<string>() === "1" ? (
            <span className="p-2 bg-green-100 rounded">Enabled</span>
          ) : (
            <span className="p-2 bg-red-100 rounded">Disabled</span>
          ),
      },
      {
        id: "toggle",
        header: "Toggle",
        enableColumnFilter: false,
        cell: ({ row }) => {
          function handleToggle(status: boolean, data: FuelStation) {
            toggleFuelLocation.mutate({ companyId, id: data.id, status: status ? 1 : 0 });
          }

          return (
            <div className="text-center">
              <input
                type="checkbox"
                defaultChecked={row.original.status === "1"}
                onChange={(e) => handleToggle(e.target.checked, row.original)}
              />
            </div>
          );
        },
      },
    ],
    [companyId, toggleFuelLocation]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="h-full flex flex-col p-4">
      {/* Table */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 overflow-auto">
          <div className="min-w-full">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const Filter = header.column.columnDef.meta?.filterComponent;
                      return (
                        <th
                          key={header.id}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50 whitespace-nowrap"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <div>{Filter ? <Filter column={header.column} /> : null}</div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
